import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FlatList, LayoutChangeEvent, ListRenderItemInfo, StyleSheet } from 'react-native';
import { FastImageProps } from 'react-native-fast-image';
import Animated, {
    Extrapolation,
    interpolate,
    useAnimatedScrollHandler,
    useAnimatedStyle,
    useDerivedValue,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';

import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { common } from '@/styles/styles';
import { showScrollIndicator } from '@/utils/constants-platform-specific';
import { triggerHapticFeedback } from '@/utils/haptic-feedback';

import {
    CENTER_VIEW_OFFSET,
    EDGE_TRANSITION_DISTANCE,
    INDICATOR_TIMING,
    LABEL_LINE_HEIGHT,
    LABEL_SELECTED_WEIGHT,
    LEAGUE_ITEM_GAP,
    LEAGUE_ITEM_WIDTH,
    LIST_GRADIENT_WIDTH,
    LIST_PADDING_BOTTOM,
    LIST_PADDING_LEFT,
    LIST_PADDING_RIGHT,
    LIST_PADDING_TOP,
    NO_SELECTION_INDICATOR_OFFSET,
    getLeaguesLayout,
} from '../utils/league-navigator-utils';
import { EdgeFade } from './EdgeFade';
import { LeagueItem } from './LeagueItem';
import { ViewAllLeagues } from './ViewAllLeagues';

export type LeagueNavigatorItem = {
    id: string;
    label: string;
    image: FastImageProps['source'];
    fitsLabel?: boolean;
    disabled?: boolean;
    testID?: string;
};

type LeagueNavigatorProps<T extends LeagueNavigatorItem> = {
    leagues: T[];
    selectedId: string;
    onSelectLeague: (league: T) => void;
    onViewAllPress: () => void;
    testID?: string;
};

const keyExtractor = (item: LeagueNavigatorItem) => item.id;

export const LeagueNavigator = <T extends LeagueNavigatorItem>({
    leagues,
    selectedId,
    onSelectLeague,
    onViewAllPress,
    testID,
}: LeagueNavigatorProps<T>) => {
    const listRef = useRef<FlatList<T>>(null);
    const [listWidth, setListWidth] = useState(0);
    const hasScrolledToSelection = useRef(false);
    const [labelWidths, setLabelWidths] = useState<Record<string, number>>({});

    const { layouts, contentWidth } = useMemo(() => getLeaguesLayout(leagues, labelWidths), [leagues, labelWidths]);
    const isMeasured = leagues.every(league => !league.fitsLabel || labelWidths[league.id] !== undefined);
    const selectedIndex = leagues.findIndex(league => league.id === selectedId);
    const selectedLayout = selectedIndex === -1 ? undefined : layouts[selectedIndex];
    const selectedOffset = selectedLayout?.offset;
    const selectedWidth = selectedLayout?.width;
    const targetIndicatorX = selectedLayout?.indicatorOffset ?? NO_SELECTION_INDICATOR_OFFSET;

    const scrollX = useSharedValue(0);
    const indicatorX = useDerivedValue(() => withTiming(targetIndicatorX, INDICATOR_TIMING), [targetIndicatorX]);

    const maxScrollX = useMemo(() => Math.max(0, contentWidth - listWidth), [contentWidth, listWidth]);

    // offsets are clamped by hand: scrollToIndex subtracts viewOffset after its own clamp and lands off by a few px
    useEffect(() => {
        if (!isMeasured || listWidth === 0 || selectedOffset === undefined || selectedWidth === undefined) {
            return;
        }
        const centeredOffset = selectedOffset - (listWidth - selectedWidth) / 2 - CENTER_VIEW_OFFSET;
        listRef.current?.scrollToOffset({
            offset: Math.min(maxScrollX, Math.max(0, centeredOffset)),
            animated: hasScrolledToSelection.current,
        });
        hasScrolledToSelection.current = true;
    }, [isMeasured, listWidth, maxScrollX, selectedOffset, selectedWidth]);

    const getItemLayout = useCallback(
        (_: ArrayLike<T> | null | undefined, index: number) => ({
            length: layouts[index].width,
            offset: layouts[index].offset,
            index,
        }),
        [layouts]
    );

    const onLayout = useCallback((event: LayoutChangeEvent) => setListWidth(event.nativeEvent.layout.width), []);

    const onScroll = useAnimatedScrollHandler({
        onScroll: event => {
            scrollX.value = event.contentOffset.x;
        },
    });

    const leftFadeProgress = useDerivedValue(() =>
        interpolate(scrollX.value, [0, EDGE_TRANSITION_DISTANCE], [0, 1], Extrapolation.CLAMP)
    );

    const rightFadeProgress = useDerivedValue(() =>
        interpolate(maxScrollX - scrollX.value, [0, EDGE_TRANSITION_DISTANCE], [0, 1], Extrapolation.CLAMP)
    );

    const allLabelStyle = useAnimatedStyle(() => {
        const progress = interpolate(
            maxScrollX - scrollX.value,
            [0, EDGE_TRANSITION_DISTANCE],
            [1, 0],
            Extrapolation.CLAMP
        );

        return {
            opacity: progress,
        };
    });

    const indicatorStyle = useAnimatedStyle(() => ({
        transform: [{ translateX: indicatorX.value - scrollX.value }],
    }));

    const onPress = useCallback(
        (league: T) => {
            triggerHapticFeedback();
            onSelectLeague(league);
        },
        [onSelectLeague]
    );

    const onLabelLayout = useCallback((id: string, event: LayoutChangeEvent) => {
        const { width } = event.nativeEvent.layout;
        setLabelWidths(widths => (widths[id] === width ? widths : { ...widths, [id]: width }));
    }, []);

    const renderItem = useCallback(
        ({ item, index }: ListRenderItemInfo<T>) => (
            <LeagueItem
                league={item}
                layout={layouts[index]}
                isSelected={index === selectedIndex}
                indicatorX={indicatorX}
                onPress={() => onPress(item)}
                testID={item.testID}
            />
        ),
        [indicatorX, layouts, onPress, selectedIndex]
    );

    return (
        <MaxWidthWrapper style={styles.container}>
            {/* sized at the selected weight so a fitted item keeps its width when it is selected */}
            <Box style={[common.measureLayer, styles.labelMeasure]}>
                {leagues
                    .filter(league => league.fitsLabel)
                    .map(league => (
                        <Text
                            key={league.id}
                            variant={'labelMedium'}
                            fontWeight={LABEL_SELECTED_WEIGHT}
                            lineHeight={LABEL_LINE_HEIGHT}
                            alignSelf={'flex-start'}
                            onLayout={event => onLabelLayout(league.id, event)}
                            accessibilityElementsHidden
                            importantForAccessibility={'no-hide-descendants'}
                        >
                            {league.label}
                        </Text>
                    ))}
            </Box>
            <Row opacity={isMeasured ? 1 : 0}>
                <Box flex={1} overflow={'hidden'} onLayout={onLayout}>
                    <EdgeFade
                        width={listWidth}
                        left={LIST_GRADIENT_WIDTH}
                        right={LIST_GRADIENT_WIDTH}
                        leftProgress={leftFadeProgress}
                        rightProgress={rightFadeProgress}
                    >
                        <Animated.View style={[styles.indicator, indicatorStyle]} />
                        <Animated.FlatList
                            ref={listRef}
                            horizontal
                            data={leagues}
                            renderItem={renderItem}
                            keyExtractor={keyExtractor}
                            getItemLayout={getItemLayout}
                            onScroll={onScroll}
                            showsHorizontalScrollIndicator={showScrollIndicator}
                            contentContainerStyle={styles.scroll}
                            testID={testID}
                        />
                    </EdgeFade>
                </Box>
                <Row>
                    <ViewAllLeagues labelStyle={allLabelStyle} onPress={onViewAllPress} />
                </Row>
            </Row>
        </MaxWidthWrapper>
    );
};

const styles = StyleSheet.create({
    container: {
        paddingTop: LIST_PADDING_TOP,
        paddingBottom: LIST_PADDING_BOTTOM,
    },
    labelMeasure: {
        pointerEvents: 'none',
    },
    scroll: {
        paddingLeft: LIST_PADDING_LEFT,
        paddingRight: LIST_PADDING_RIGHT,
        gap: LEAGUE_ITEM_GAP,
    },
    indicator: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: LEAGUE_ITEM_WIDTH,
        height: LEAGUE_ITEM_WIDTH,
        borderRadius: 12,
        backgroundColor: ds.colorValue.background.neutral.subtle,
        borderWidth: 1,
        borderColor: ds.colorValue.border.subtle,
        pointerEvents: 'none',
    },
});
